/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Address,
  Edge,
  EdgeType,
} from '@/models';

import { fetchCustomer } from './customer';
import { fetchEdgesTo } from './edge';

export async function fetchAddress(id: string): Promise<Address> {
  const customerPromise = fetchCustomer();

  // Search customer address.
  const customerAddress = customerPromise.then((customer) => {
    if (!customer) {
      return;
    }

    // Search customer.address.
    return customer.addresses.find((address) => (address.id === id));
  });

  // Search addresses in the edges that are related to the customer.
  const edgeAddress = customerPromise.then((customer) => {
    if (!customer) {
      return;
    }

    return fetchEdgesTo<Address[]>(
      EdgeType.CustomerAddressesForFulfillmentService, customer.id).then((edges) => {
        for (const edge of edges) {
          const address = edge.data.find((addr) => (addr.id === id));
          if (address) {
            return address;
          }
        }
        return;
      });
  });

  // Resolve to the first promise that returns address.
  //
  // See https://stackoverflow.com/questions/51160260/clean-way-to-wait-for-first-true-returned-by-promise.
  return Promise.race([
    new Promise<Address>((resolve, reject) => (
      customerAddress.then((address) => address && resolve(address), reject))),
    new Promise<Address>((resolve, reject) => (
      edgeAddress.then((address) => address && resolve(address), reject))),

    Promise.all([customerAddress, edgeAddress]).then((addresses) => {
      const address = addresses.find((addr) => (!!addr));
      if (!address) {
        throw new Error(`cannot find address: ${id}`);
      }
      return address;
    }),
  ]);
}
